import type { QuoteProduct, QuoteUnit } from '~/store/quotes/interfaces';

export const QUOTE_PRODUCT_LABEL: Record<QuoteProduct, string> = {
  soja: 'Soja',
  milho: 'Milho',
  boi_gordo: 'Boi Gordo',
  dolar: 'Dólar',
};

export const QUOTE_PRODUCT_SYMBOL: Record<QuoteProduct, string> = {
  soja: 'SOJA',
  milho: 'MILHO',
  boi_gordo: 'BOI',
  dolar: 'USD',
};

export const QUOTE_PRODUCT_COLOR: Record<QuoteProduct, string> = {
  soja: '#3F7D5F',
  milho: '#E6A82E',
  boi_gordo: '#A6612B',
  dolar: '#00963D',
};

const UNIT_SUFFIX: Record<string, string> = {
  sc60: '/sc',
  '@': '/@',
  BRL: '',
};

export function unitSuffix(unit: QuoteUnit): string {
  return UNIT_SUFFIX[unit] ?? '';
}

export function unitFullLabel(unit: QuoteUnit): string {
  switch (unit) {
    case 'sc60':
      return 'por saca de 60kg';
    case '@':
      return 'por arroba';
    case 'BRL':
      return 'comercial';
    default:
      return '';
  }
}

export function formatQuotePrice(price: number, _unit: QuoteUnit): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(price) ? price : 0);
}

export function formatChangePct(pct: number): string {
  let sign = '';
  if (pct > 0) sign = '+';
  else if (pct < 0) sign = '−';
  return `${sign}${Math.abs(pct).toFixed(2).replace('.', ',')}%`;
}

export type ChangeDirection = 'up' | 'down' | 'flat';

export function changeDirection(pct?: number | null): ChangeDirection {
  if (pct == null || pct === 0) return 'flat';
  return pct > 0 ? 'up' : 'down';
}

export function pctColorClass(pct?: number | null): string {
  const dir = changeDirection(pct);
  if (dir === 'up') return 'color-success-05';
  if (dir === 'down') return 'color-warning-04';
  return 'color-intense-05';
}

export type WeatherKind = 'clear' | 'cloud' | 'rain' | 'storm' | 'fog';

export function weatherKind(slug?: string | null): WeatherKind {
  if (!slug) return 'clear';
  if (slug.includes('storm')) return 'storm';
  if (slug.includes('rain') || slug.includes('hail')) return 'rain';
  if (slug.includes('fog') || slug.includes('mist')) return 'fog';
  if (slug.includes('cloud') || slug.includes('cloudly')) return 'cloud';
  return 'clear';
}

export interface UfOption {
  uf: string;
  name: string;
}

export const BR_STATES: UfOption[] = [
  { uf: 'AC', name: 'Acre' },
  { uf: 'AL', name: 'Alagoas' },
  { uf: 'AP', name: 'Amapá' },
  { uf: 'AM', name: 'Amazonas' },
  { uf: 'BA', name: 'Bahia' },
  { uf: 'CE', name: 'Ceará' },
  { uf: 'DF', name: 'Distrito Federal' },
  { uf: 'ES', name: 'Espírito Santo' },
  { uf: 'GO', name: 'Goiás' },
  { uf: 'MA', name: 'Maranhão' },
  { uf: 'MT', name: 'Mato Grosso' },
  { uf: 'MS', name: 'Mato Grosso do Sul' },
  { uf: 'MG', name: 'Minas Gerais' },
  { uf: 'PA', name: 'Pará' },
  { uf: 'PB', name: 'Paraíba' },
  { uf: 'PR', name: 'Paraná' },
  { uf: 'PE', name: 'Pernambuco' },
  { uf: 'PI', name: 'Piauí' },
  { uf: 'RJ', name: 'Rio de Janeiro' },
  { uf: 'RN', name: 'Rio Grande do Norte' },
  { uf: 'RS', name: 'Rio Grande do Sul' },
  { uf: 'RO', name: 'Rondônia' },
  { uf: 'RR', name: 'Roraima' },
  { uf: 'SC', name: 'Santa Catarina' },
  { uf: 'SP', name: 'São Paulo' },
  { uf: 'SE', name: 'Sergipe' },
  { uf: 'TO', name: 'Tocantins' },
];

export function ufName(uf?: string | null): string {
  if (!uf) return 'Nacional';
  return BR_STATES.find((s) => s.uf === uf)?.name ?? uf;
}

export const UF_CAPITAL: Record<string, string> = {
  AC: 'Rio Branco',
  AL: 'Maceió',
  AP: 'Macapá',
  AM: 'Manaus',
  BA: 'Salvador',
  CE: 'Fortaleza',
  DF: 'Brasília',
  ES: 'Vitória',
  GO: 'Goiânia',
  MA: 'São Luís',
  MT: 'Cuiabá',
  MS: 'Campo Grande',
  MG: 'Belo Horizonte',
  PA: 'Belém',
  PB: 'João Pessoa',
  PR: 'Curitiba',
  PE: 'Recife',
  PI: 'Teresina',
  RJ: 'Rio de Janeiro',
  RN: 'Natal',
  RS: 'Porto Alegre',
  RO: 'Porto Velho',
  RR: 'Boa Vista',
  SC: 'Florianópolis',
  SP: 'São Paulo',
  SE: 'Aracaju',
  TO: 'Palmas',
};

export function weatherCityForUf(uf?: string | null): string | undefined {
  if (!uf) return undefined;
  const cap = UF_CAPITAL[uf];
  return cap ? `${cap},${uf}` : undefined;
}

export function relativeFromNow(iso?: string | null): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(diff) || diff < 0) return 'agora';
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min} min`;
  const hours = Math.floor(min / 60);
  if (hours < 24) return `há ${hours} h`;
  const days = Math.floor(hours / 24);
  return `há ${days} d`;
}
